import { useCallback, useEffect, useState } from "react"
import type { WorkerScenario } from "@/components/ats-fbo/types"

export interface UseConfirmableEmailEditProps {
  /** When non-null, editing the email is destructive — setWorkerEmail wipes the worker and
   *  worker organization. */
  workerScenario: WorkerScenario | null
  contactOptionsCount: number
}

export interface UseConfirmableEmailEditReturn {
  /** True while the inline "discard worker/org?" prompt is shown above the email field. */
  pendingEmailEdit: boolean
  /** Bumped on confirm — wired to ConfirmableEmailField's `forceStartEditingTrigger` so
   *  it knows to enter edit mode now that the user has accepted the discard. Number-bumping
   *  rather than a boolean toggle so repeats of the same flow keep firing. */
  forceEditTrigger: number
  hasExistingContactsForEmail: boolean
  /** Wired to ConfirmableEmailField's `onAttemptStartEdit`. Returns false to block the
   *  pencil from entering edit mode and surfaces the confirmation; returns true when no
   *  scenario would be discarded. */
  onAttemptStartEdit: () => boolean
  /** Wired to the prompt's "Yes, edit email" button. */
  confirm: () => void
  /** Wired to the prompt's "Cancel" button. */
  cancel: () => void
}

/**
 * Email change is destructive in the same way as profile-type — setWorkerEmail wipes the
 * selected worker + organization. Intercept the PENCIL click (rather than the ✓ commit)
 * so the user sees the warning before they ever start typing. The hook returns
 * `onAttemptStartEdit` (which ConfirmableEmailField uses to gate the pencil) plus a
 * `forceEditTrigger` counter that signals the field to actually enter edit mode after the
 * user accepts the prompt. Auto-clears when workerScenario itself drops (e.g. via the
 * email change committing later).
 */
export function useConfirmableEmailEdit({
  workerScenario,
  contactOptionsCount,
}: UseConfirmableEmailEditProps): UseConfirmableEmailEditReturn {
  const [pendingEmailEdit, setPendingEmailEdit] = useState(false)
  const [forceEditTrigger, setForceEditTrigger] = useState(0)
  const hasExistingContactsForEmail = contactOptionsCount > 0

  useEffect(() => {
    if (workerScenario == null) setPendingEmailEdit(false)
  }, [workerScenario])

  const onAttemptStartEdit = useCallback(() => {
    if (workerScenario != null) {
      setPendingEmailEdit(true)
      return false
    }
    return true
  }, [workerScenario])

  const confirm = useCallback(() => {
    setPendingEmailEdit(false)
    setForceEditTrigger((t) => t + 1)
  }, [])

  const cancel = useCallback(() => {
    setPendingEmailEdit(false)
  }, [])

  return {
    pendingEmailEdit,
    forceEditTrigger,
    hasExistingContactsForEmail,
    onAttemptStartEdit,
    confirm,
    cancel,
  }
}
